import React from 'react';

import { cn } from './cn';
import { FormFieldset } from './FormFieldset';

export interface RadioOption {
  key: string;
  text: string;
  disabled?: boolean;
  /** Supporting text below the option title. Only rendered by the `cards` variant. */
  description?: string;
}

export interface RadioGroupProps {
  label?: string;
  description?: string;
  errorMessage?: string;
  successMessage?: string;
  required?: boolean;
  className?: string;
  name?: string;
  options: RadioOption[];
  selectedKey?: string;
  onChange?: (key: string) => void;
  disabled?: boolean;
  /** Lay options out in a row instead of a column. */
  horizontal?: boolean;
  optionsClassName?: string;
  /**
   * `default` renders plain radio rows; `cards` renders each option as a selectable card
   * (title + description) while keeping native radio semantics.
   */
  variant?: 'default' | 'cards';
}

export const RadioGroup = ({
  label,
  description,
  errorMessage,
  successMessage,
  required,
  className,
  name,
  options,
  selectedKey,
  onChange,
  disabled,
  horizontal,
  optionsClassName,
  variant = 'default',
}: RadioGroupProps) => {
  const generatedId = React.useId();
  const groupName = name || generatedId;

  return (
    <FormFieldset
      legend={label}
      description={description}
      errorMessage={errorMessage}
      successMessage={successMessage}
      required={required}
      className={className}
    >
      <div
        className={cn(
          'flex gap-3',
          horizontal ? 'flex-row flex-wrap' : 'flex-col',
          variant === 'cards' && 'gap-2',
          optionsClassName
        )}
      >
        {options.map((option) => {
          const optionId = `${groupName}-${option.key}`;
          const isDisabled = disabled || option.disabled;
          if (variant === 'cards') {
            const selected = selectedKey === option.key;
            return (
              <label
                key={option.key}
                htmlFor={optionId}
                className={cn(
                  'flex-1 min-w-[240px] rounded-lg border p-3 text-left transition-colors select-none',
                  'focus-within:ring-2 focus-within:ring-info-500/60 focus-within:ring-offset-2',
                  'dark:focus-within:ring-offset-surface-raised',
                  selected ? 'border-primary-500 bg-primary-100/50 dark:bg-primary-500/15' : 'border-border bg-surface',
                  isDisabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer hover:border-primary-400'
                )}
              >
                <input
                  id={optionId}
                  type="radio"
                  name={groupName}
                  value={option.key}
                  checked={selected}
                  disabled={isDisabled}
                  onChange={() => onChange?.(option.key)}
                  className="sr-only"
                />
                <div className="text-sm font-medium text-text-primary">{option.text}</div>
                {option.description && <div className="mt-1 text-xs text-text-muted">{option.description}</div>}
              </label>
            );
          }
          return (
            <div key={option.key} className="flex items-center gap-2">
              <input
                id={optionId}
                type="radio"
                name={groupName}
                value={option.key}
                checked={selectedKey === option.key}
                disabled={isDisabled}
                onChange={() => onChange?.(option.key)}
                className={cn(
                  'h-4 w-4 shrink-0 cursor-pointer accent-status-info-emphasis',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-info-500/60 focus-visible:ring-offset-2',
                  'dark:focus-visible:ring-offset-surface-raised',
                  isDisabled && 'cursor-not-allowed opacity-50'
                )}
              />
              <label
                htmlFor={optionId}
                // Disabled labels mute one step instead of `opacity-50` — halving a light text
                // color on a dark surface drops it below legibility.
                className={cn(
                  'text-sm select-none',
                  isDisabled ? 'text-text-secondary cursor-not-allowed' : 'text-text-primary cursor-pointer'
                )}
              >
                {option.text}
              </label>
            </div>
          );
        })}
      </div>
    </FormFieldset>
  );
};
